// App.tsx — Root router for the technical recipe site. The shared Shell from
// lib/web supplies the navbar, the Darkly/Flatly theme toggle, and the page
// Container, so every route renders inside the Axios chrome.
// Routes: recipe list (home), individual recipe, and login redirect.
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { BsBook } from 'react-icons/bs'
import { Shell, OidcLoginPage } from 'common'
import type { ShellNavItem } from 'common'
import { useAuth, logout } from './hooks/useAuth'
import RecipeListPage from './pages/RecipeListPage'
import RecipePage from './pages/RecipePage'
import RecipeEditPage from './pages/RecipeEditPage'

function Root() {
    const auth = useAuth()

    // Recipes are public; the sign-in entry only appears while signed out.
    const navItems: ShellNavItem[] = [{ label: 'Recipes', href: '/' }]
    if (!auth.loading && !auth.authenticated) {
        navItems.push({ label: 'Sign In', href: '/login' })
    }

    const brand = (
        <>
            <BsBook size={18} /> Cooking
        </>
    )

    return (
        <Shell
            brand={brand}
            navItems={navItems}
            userEmail={auth.authenticated ? auth.email : undefined}
            userPhotoUrl={auth.photoUrl || undefined}
            onLogout={auth.authenticated ? logout : undefined}
        >
            <Routes>
                <Route path="/" element={<RecipeListPage />} />
                <Route path="/recipe/:id" element={<RecipePage />} />
                {/* Authoring routes. The server enforces ownership on every
                    write; these routes just decide what the UI offers. */}
                <Route path="/new" element={<RecipeEditPage />} />
                <Route path="/recipe/:id/edit" element={<RecipeEditPage />} />
                {/* returnTo is pinned to "/" and never the current path: landing
                    back on /login would re-trigger this page's redirect and loop
                    forever. OidcLoginPage also breaks out after 3 rapid attempts
                    if the session is issued but rejected. */}
                <Route path="/login" element={<OidcLoginPage returnTo="/" />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Shell>
    )
}

function App() {
    return (
        <BrowserRouter>
            <Root />
        </BrowserRouter>
    )
}

export default App
